import { KEYBINDS } from '../../config/constants.js';
import { audio } from '../systems/audio/AudioSystem.js';
import { isMobileModeActive } from '../core/deviceMode.js';

// KeyboardEvent.code -> a short, human-readable label.
const KEY_LABELS = {
  KeyW: 'W', KeyA: 'A', KeyS: 'S', KeyD: 'D',
  ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→',
  ShiftLeft: 'Shift', ShiftRight: 'Shift',
  Escape: 'Esc', KeyP: 'P', Space: 'Space',
  KeyQ: 'Q', KeyE: 'E', KeyC: 'C', KeyF: 'F',
  Digit1: '1', Digit2: '2', Digit3: '3',
};

function formatKeys(codes) {
  // De-dupes e.g. ShiftLeft/ShiftRight both labeling as "Shift".
  const labels = [...new Set(codes.map((c) => KEY_LABELS[c] ?? c))];
  return labels.join(' / ');
}

/** Read-only reference screen listing the current keybinds (config/constants.js's KEYBINDS). */
export class ControlsMenu {
  constructor(root, save, { onBack }) {
    this.root = root;
    this.save = save;
    this.onBack = onBack;
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay';
    this._render();
    this.root.appendChild(this.el);
  }

  _render() {
    this.el.innerHTML = '';

    const title = document.createElement('div');
    title.className = 'menu-title';
    title.textContent = 'CONTROLS';
    this.el.appendChild(title);

    const rows = document.createElement('div');
    rows.className = 'settings-rows';
    rows.appendChild(this._buildRow('Move', `${formatKeys(KEYBINDS.up)} ${formatKeys(KEYBINDS.left)} ${formatKeys(KEYBINDS.down)} ${formatKeys(KEYBINDS.right)}`));
    rows.appendChild(this._buildRow('Dash', formatKeys(KEYBINDS.dash)));
    rows.appendChild(this._buildRow('Focus (hold)', formatKeys(KEYBINDS.focus)));
    rows.appendChild(this._buildRow('Pause', formatKeys(KEYBINDS.pause)));
    rows.appendChild(this._buildRow('Champion Ultimate', formatKeys(KEYBINDS.ultimate)));
    // One key, two champions, never both in the same run: Fugue swaps stance
    // with it and Tine flips attunement with it (data/characters.js). Named
    // for what it is rather than for either mechanic — a player picking a
    // champion that doesn't use it shouldn't be reading about stances.
    rows.appendChild(this._buildRow('Champion Key (Fugue / Tine)', formatKeys(KEYBINDS.stanceSwap)));
    rows.appendChild(this._buildRow('Level-Up Pick', formatKeys(KEYBINDS.levelUpPick)));
    this.el.appendChild(rows);

    // Focus is the one binding whose key doesn't explain itself — "Dash" and
    // "Pause" do, "Focus" doesn't say what it buys you.
    const focusNote = document.createElement('div');
    focusNote.className = 'menu-hint';
    focusNote.style.textAlign = 'center';
    // Follows the Toggle Focus/Dash setting: a reference screen that tells you
    // to hold a key you have configured to tap is worse than no note at all.
    const verb = this.save?.toggleHold ? 'Tap Focus to toggle' : 'Hold Focus to';
    focusNote.textContent = `${verb} walk slowly and reveal your true hitbox (the white dot) plus the graze ring around it. Bullets that cross the ring without touching the dot charge your ultimate.`;
    this.el.appendChild(focusNote);

    // Mobile mode gets an on-screen joystick + buttons instead (see
    // ui/TouchControls.js) — only worth mentioning when they'll actually be
    // shown (device auto-detect, or the player's own Mobile Controls choice
    // on champion select — core/deviceMode.js), not to mouse+keyboard players.
    if (isMobileModeActive(this.save)) {
      const touchNote = document.createElement('div');
      touchNote.className = 'menu-hint';
      touchNote.style.textAlign = 'center';
      touchNote.textContent = 'Mobile Controls active: use the on-screen joystick to move, and the ⚡ Dash / ★ Ultimate / ❙❙ Pause buttons during a run.';
      this.el.appendChild(touchNote);
    }

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  _buildRow(label, keys) {
    const row = document.createElement('div');
    row.className = 'settings-row';

    const name = document.createElement('span');
    name.className = 'settings-row-name';
    name.textContent = label;
    row.appendChild(name);

    const value = document.createElement('span');
    value.className = 'settings-row-value';
    value.textContent = keys;
    row.appendChild(value);

    return row;
  }

  destroy() {
    this.el.remove();
  }
}
